import React, { useState, useEffect } from "react";
import { Form, Button, Dropdown } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { Employee, Area, ContractType, Ocupacion } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { BasicModal } from "../../../Shared";
import { ContractTypeForm } from "../../ContractTypes/ContractTypeForm/ContractTypeForm";
import "./EmployeeForm.scss";

const empController          = new Employee();
const areaController         = new Area();
const contractTypeCtrl       = new ContractType();
const ocupacionCtrl          = new Ocupacion();

export function EmployeeForm({ employee, close, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [areaOptions,         setAreaOptions]         = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [ocupacionOptions,    setOcupacionOptions]    = useState([]);
  const [showCTModal,         setShowCTModal]         = useState(false);

  const loadContractTypes = async (selectNewName = null) => {
    try {
      const res = await contractTypeCtrl.getContractTypes(accessToken, true);
      const opts = [
        ...res.map((c) => ({ key: c._id, value: c._id, text: c.name })),
        { key: "__add__", value: "__add__", text: t("new_contract_type"), className: "add-ct-option" },
      ];
      setContractTypeOptions(opts);
      if (selectNewName) {
        const added = opts.find((o) => o.text === selectNewName);
        if (added) formik.setFieldValue("contractType", added.value);
      }
    } catch {
      setContractTypeOptions([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const areas = await areaController.getAreas(accessToken, true);
        setAreaOptions(areas.map((a) => ({ key: a._id, value: a._id, text: a.name })));
      } catch {
        setAreaOptions([]);
      }
    })();
    loadContractTypes();
    ocupacionCtrl.getOcupaciones(accessToken, true)
      .then((res) => setOcupacionOptions(res.map((o) => ({ key: o._id, value: o._id, text: o.name }))))
      .catch(() => setOcupacionOptions([]));
  }, []);

  const formik = useFormik({
    initialValues: {
      numero_legajo: employee?.numero_legajo || "",
      nombre:        employee?.nombre        || "",
      apellido:      employee?.apellido      || "",
      dni:           employee?.dni           || "",
      cargo:         employee?.cargo         || "",
      area:          employee?.area?._id     || employee?.area || "",
      contractType:  employee?.contractType?._id || employee?.contractType || "",
      occupation:    employee?.occupation?._id    || employee?.occupation   || "",
      fecha_ingreso: employee?.fecha_ingreso ? employee.fecha_ingreso.slice(0, 10) : "",
      fecha_egreso:  employee?.fecha_egreso  ? employee.fecha_egreso.slice(0, 10)  : "",
      observations:  employee?.observations  || "",
      tipo:          "permanente",
    },
    validationSchema: Yup.object({
      numero_legajo: Yup.string().required("El número de legajo es requerido"),
      nombre:        Yup.string().required("El nombre es requerido"),
      apellido:      Yup.string().required("El apellido es requerido"),
    }),
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = { ...values };
        if (!payload.numero_legajo) delete payload.numero_legajo;
        if (!payload.area)          delete payload.area;
        if (!payload.fecha_ingreso) delete payload.fecha_ingreso;
        if (!payload.fecha_egreso)  delete payload.fecha_egreso;
        if (employee) {
          await empController.updateEmployee(accessToken, employee._id, payload);
          toast.success(t("employee_updated"));
        } else {
          await empController.createEmployee(accessToken, payload);
          toast.success(t("employee_created"));
        }
        onReload();
        close();
      } catch (err) {
        toast.error(err?.msg || t("error_saving"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const cap = (v) => v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v;

  return (
    <Form className="employee-form" onSubmit={formik.handleSubmit}>
      <ToastContainer position="top-center" autoClose={800} hideProgressBar />

      <Form.Input
        label={<label>{t("record_number")} <span style={{ color: "#C0392B" }}>*</span></label>}
        name="numero_legajo"
        value={formik.values.numero_legajo}
        onChange={formik.handleChange}
        error={formik.errors.numero_legajo}
        placeholder="Ej: 1024"
      />

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("first_name")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="nombre"
          value={formik.values.nombre}
          onChange={(e) => formik.setFieldValue("nombre", cap(e.target.value))}
          error={formik.errors.nombre}
          placeholder="Ej: Juan"
        />
        <Form.Input
          label={<label>{t("last_name")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="apellido"
          value={formik.values.apellido}
          onChange={(e) => formik.setFieldValue("apellido", cap(e.target.value))}
          error={formik.errors.apellido}
          placeholder="Ej: Pérez"
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label="DNI"
          name="dni"
          value={formik.values.dni}
          onChange={formik.handleChange}
          placeholder="Ej: 30123456"
        />
        <Form.Input
          label={t("job_title")}
          name="cargo"
          value={formik.values.cargo}
          onChange={formik.handleChange}
          placeholder="Ej: Operario"
        />
      </Form.Group>

      <Form.Field>
        <label>{t("area")}</label>
        <Dropdown
          selection fluid clearable search
          options={areaOptions}
          value={formik.values.area}
          onChange={(_, { value }) => formik.setFieldValue("area", value)}
          placeholder={t("select_area")}
          noResultsMessage={t("no_results")}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("contract_type")}</label>
        <Dropdown
          selection fluid clearable search
          options={contractTypeOptions}
          value={formik.values.contractType}
          onChange={(_, { value }) => {
            if (value === "__add__") { setShowCTModal(true); return; }
            formik.setFieldValue("contractType", value);
          }}
          placeholder={t("select_type")}
          noResultsMessage={t("no_results")}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("occupation")}</label>
        <Dropdown
          selection fluid clearable search
          options={ocupacionOptions}
          value={formik.values.occupation}
          onChange={(_, { value }) => formik.setFieldValue("occupation", value)}
          placeholder={t("select_occupation")}
          noResultsMessage={t("no_results")}
        />
      </Form.Field>

      <Form.Group widths="equal">
        <Form.Input
          label={t("entry_date")}
          name="fecha_ingreso"
          type="date"
          value={formik.values.fecha_ingreso}
          onChange={formik.handleChange}
        />
        <Form.Input
          label={t("exit_date")}
          name="fecha_egreso"
          type="date"
          value={formik.values.fecha_egreso}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <Form.TextArea
        label={t("observations")}
        name="observations"
        value={formik.values.observations}
        onChange={formik.handleChange}
        placeholder={t("additional_observations")}
        rows={3}
      />

      <div className="employee-form__actions">
        <button className="employee-form__btn employee-form__btn--primary" type="submit" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? "…" : (employee ? t("update") : t("save"))}
        </button>
        <button className="employee-form__btn employee-form__btn--cancel" type="button" onClick={close}>{t("cancel")}</button>
      </div>

      <BasicModal
        show={showCTModal}
        close={() => setShowCTModal(false)}
        title={t("new_contract_type_title")}
        size="small"
      >
        <ContractTypeForm
          close={() => setShowCTModal(false)}
          onReload={async () => {
            setShowCTModal(false);
            await loadContractTypes();
          }}
        />
      </BasicModal>
    </Form>
  );
}
